import type { CityConfig } from "../city-template/types";

export const chulaVistaConfig: CityConfig = {
  city: "Chula Vista",
  state: "California",
  stateAbbr: "CA",
  slug: "chula-vista",
  route: "/cities/chula-vista",
  title: "Emergency Passport Chula Vista, CA | Fast Passport Renewal Services",
  metaDescription: "Emergency passport and expedited passport services in Chula Vista, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for South Bay San Diego travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Chula Vista, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Chula Vista and South Bay San Diego travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Chula%20Vista%20California%20skyline%20at%20golden%20hour%20with%20Otay%20Ranch%20shopping%20center%20and%20modern%20suburban%20developments%2C%20San%20Diego%20Bay%20in%20the%20background%2C%20warm%20sunset%20light%20with%20coastal%20mountains%20and%20palm%20trees%2C%20professional%20cityscape%20photography%2C%20clean%20modern%20residential%20landscape%2C%20no%20visible%20text%20or%20people%2C%20serene%20South%20Bay%20atmosphere&width=1800&height=700&seq=chula-vista-hero-001&orientation=landscape",
  heroImageAlt: "Chula Vista California skyline at golden hour with South Bay landscape and San Diego Bay",
  geoPosition: "32.6401;-117.0842",
  geoPlacename: "Chula Vista, California",
  regionalAgency: {
    name: "San Diego Passport Agency",
    address: "401 West A St, San Diego, CA 92101",
    distance: "Located in downtown San Diego — approximately 10–15 minutes from Chula Vista via I-5",
  },
  nearbyCitiesZips: [
    { name: "Chula Vista", zips: "91909–91915, 91921" },
    { name: "National City", zips: "91950, 91951" },
    { name: "Imperial Beach", zips: "91932, 91933" },
    { name: "Bonita", zips: "91902" },
    { name: "San Ysidro", zips: "92143, 92173" },
    { name: "Coronado", zips: "92118, 92178" },
    { name: "San Diego", zips: "92101–92117" },
    { name: "La Mesa", zips: "91941, 91942, 91943, 91944" },
    { name: "El Cajon", zips: "92019–92022" },
    { name: "Santee", zips: "92071, 92072" },
    { name: "Lemon Grove", zips: "91945, 91946" },
    { name: "Spring Valley", zips: "91977, 91978" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent South Bay departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in San Diego", route: "/cities/san-diego" },
    { name: "Expedited passport services in Los Angeles", route: "/cities/los-angeles" },
    { name: "Emergency passport help in Irvine", route: "/cities/irvine" },
    { name: "Urgent passport services near Santa Ana", route: "/cities/santa-ana" },
    { name: "Same-day passport help in Anaheim", route: "/cities/anaheim" },
    { name: "Rush passport services in Long Beach", route: "/cities/long-beach" },
    { name: "Passport courier services in Pasadena", route: "/cities/pasadena" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Chula Vista remotely via secure courier. We do not have a walk-in office in Chula Vista — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for South Bay San Diego travelers.",
  fpcOfficeDistance: "Serving all of South Bay San Diego",
  serviceAreaIntro: "From the Chula Vista Bayfront to Otay Ranch, from Eastlake to the Olympic Training Center — Fast Passport Center serves the entire Chula Vista community. We regularly help travelers from National City, Imperial Beach, Bonita, and San Ysidro get their passports fast.",
  nearbyCitiesIntro: "We serve all Chula Vista neighborhoods and nearby South Bay cities including National City, Imperial Beach, Coronado, Bonita, and San Ysidro. If you are in the San Diego South Bay or near the border, we have you covered.",
  howItWorksIntro: "Most Chula Vista travelers complete everything online and ship documents overnight. No need to drive to downtown San Diego or wait in line at the regional agency.",
  mistakesIntro: "With San Diego International Airport (SAN) just 15 minutes away and the Mexico border crossing at San Ysidro, Chula Vista travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Chula Vista and South Bay travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Chula Vista? Here are the most common ones we hear from South Bay San Diego travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Chula Vista international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Carlos M.",
      location: "Chula Vista, CA",
      rating: 5,
      text: "My family trip to Puerto Vallarta was in 2 days and my passport was expired. Fast Passport Center got me a new one in 30 hours. Incredible service for the South Bay.",
      service: "Emergency Passport",
    },
    {
      name: "Angela R.",
      location: "National City, CA",
      rating: 5,
      text: "Needed to renew for a cruise departing from San Diego. The process was incredibly smooth, and I had my passport in 4 days. Highly recommend for South Bay residents.",
      service: "Passport Renewal",
    },
    {
      name: "David S.",
      location: "Imperial Beach, CA",
      rating: 5,
      text: "Lost my passport a week before my honeymoon to Cancun. They walked me through the DS-64 process and got me a replacement in 48 hours. Saved our trip.",
      service: "Lost Passport",
    },
    {
      name: "Patricia L.",
      location: "Bonita, CA",
      rating: 5,
      text: "As a frequent cross-border traveler, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable for South Bay residents.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "chula-vista-1",
      name: "Chula Vista Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "815 E St",
      city: "Chula Vista",
      state: "CA",
      zip: "91910",
      phone: "(619) 422-1624",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Chula Vista",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "chula-vista-2",
      name: "National City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1200 E Plaza Blvd",
      city: "National City",
      state: "CA",
      zip: "91950",
      phone: "(619) 477-2322",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.5 miles from downtown National City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "chula-vista-3",
      name: "San Diego Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2535 Midway Dr",
      city: "San Diego",
      state: "CA",
      zip: "92110",
      phone: "(619) 692-2701",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8.5 miles from Chula Vista",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for San Diego. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "chula-vista-4",
      name: "Imperial Beach Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1271 Palm Ave",
      city: "Imperial Beach",
      state: "CA",
      zip: "91932",
      phone: "(619) 424-4124",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Imperial Beach Pier",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for South Bay coastal residents. Book appointments ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "chula-vista-5",
      name: "La Mesa Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8841 La Mesa Blvd",
      city: "La Mesa",
      state: "CA",
      zip: "91942",
      phone: "(619) 464-7452",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.0 miles from La Mesa Village",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for eastern South Bay residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "chula-vista-6",
      name: "San Diego County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "1600 Pacific Hwy",
      city: "San Diego",
      state: "CA",
      zip: "92101",
      phone: "(619) 237-0502",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "10 miles from Chula Vista",
      website: "https://www.sandiegocounty.gov/",
      websiteLabel: "SD County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Diego", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "South Bay photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at San Diego acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many South Bay applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Diego regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many South Bay families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "South Bay travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Chula Vista." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Chula Vista address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Chula Vista before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Chula Vista for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most South Bay San Diego travelers." },
    { q: "How fast can I renew my passport in Chula Vista?", a: "Fast Passport Center offers fast passport renewal in Chula Vista in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Chula Vista in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Chula Vista?", a: "Same-day passport appointments are only available at the San Diego Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to South Bay residents." },
    { q: "Can I expedite a child passport in Chula Vista?", a: "Yes. Fast Passport Center expedites child passports in Chula Vista for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk South Bay San Diego families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Chula Vista with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Chula Vista?",
      a: "With Fast Passport Center's emergency courier service, Chula Vista travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the San Diego Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Chula Vista?",
      a: "Popular options include the Chula Vista Main Post Office (91910), National City Post Office (91950), and San Diego Main Post Office (92110). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Chula Vista?",
      a: "Same-day passports are only available at the San Diego Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live near the Mexico border or in Imperial Beach?",
      a: "We serve all of South Bay San Diego including National City, Imperial Beach, San Ysidro, Bonita, and Coronado. Our overnight shipping labels work from any UPS location, so your location within the South Bay doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Chula Vista?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Chula Vista?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Chula Vista and National City offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Chula Vista?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Chula Vista?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk South Bay families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Chula Vista", address: "395 E St, Chula Vista, CA 91910", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — National City", address: "1000 E Plaza Blvd, National City, CA 91950", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Chula Vista", address: "2015 Birch Rd, Chula Vista, CA 91915", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Otay Ranch", address: "2015 Birch Rd, Chula Vista, CA 91915", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — San Ysidro", address: "3330 Camino De La Plaza, San Ysidro, CA 92173", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Imperial Beach", address: "650 Palm Ave, Imperial Beach, CA 91932", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Bonita", address: "4374 Bonita Rd, Bonita, CA 91902", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — San Diego", address: "2985 Cedar St, San Diego, CA 92102", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Chula Vista",
      address: "30 N Glover Ave, Chula Vista, CA 91910",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — San Diego",
      address: "3960 Normal St, San Diego, CA 92103",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — El Cajon",
      address: "1450 Magnolia Ave, El Cajon, CA 92020",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — San Ysidro",
      address: "6110 Mountain View Dr, San Diego, CA 92113",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Convenient for border-area residents. Full-service center.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SAN",
      name: "San Diego International Airport",
      distance: "12 miles from downtown Chula Vista",
      address: "3225 N Harbor Dr, San Diego, CA 92101",
      note: "The primary international airport for San Diego. International flights to Mexico, Canada, Europe, and Asia.",
      flightUrl: "https://www.san.org/",
      flightLabel: "San Diego International",
      featured: true,
    },
    {
      code: "TIJ",
      name: "Tijuana International Airport",
      distance: "8 miles from Chula Vista via border crossing",
      address: "Carretera Aeropuerto, Tijuana, BC 22435",
      note: "Cross-border airport option for Mexico and international travel. Cross Border Xpress (CBX) bridge connects directly.",
      flightUrl: "https://www.aeropuerto.gob.mx/tijuana/",
      flightLabel: "Tijuana Airport",
      featured: false,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "120 miles from Chula Vista",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub accessible via I-5. For long-haul flights to Asia, Europe, and South America.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
  ],
};